import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';

/**
 * THE SHELF, READ ONCE — what every stock screen shares.
 *
 * `GET /stock` already answers the whole of f1: each item with what is on it,
 * its lots earliest-first (FEFO), whether it is low, whether something on it
 * expires soon, and a MEASURED monthly rate from the last 90 days of
 * dispensing. Sorting and the two totals are the server's, so a second screen
 * cannot disagree with the first about what is low.
 *
 * Nothing here filters. The chips on the board filter what has already
 * arrived, because "low OR expiring" over a clinic's own shelf is a handful of
 * rows, and a filter round-trip per chip would make the shelf feel slower than
 * the shelf.
 */

export interface ShelfLot {
  id: string;
  lotCode: string | null;
  expiresOn: string | null;
  quantity: number;
}

export interface ShelfItem {
  id: string;
  name: string;
  category: string | null;
  unit: string | null;
  batchTracked: boolean;
  minLevel: number | null;
  barcode: string | null;
  photoUrl: string | null;
  /** Linked to something Reception sells, where its price and tax live. */
  sellable: boolean;
  inStock: number;
  /** Rough monthly use, measured. Null when nothing has been dispensed yet. */
  perMonth: number | null;
  nextExpiry: string | null;
  expiringSoon: boolean;
  low: boolean;
  lots: ShelfLot[];
}

export interface Movement {
  id: string;
  lotId: string | null;
  delta: number;
  reason: string;
  reference: string | null;
  note: string | null;
  at: string | null;
  actorName: string | null;
}

const unwrap = <T,>(body: unknown): T => ((body as { data?: T })?.data ?? body) as T;

export function useShelf() {
  return useQuery({
    queryKey: ['stock', 'shelf'],
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<ShelfItem[]> => {
      const res = await apiClient.get('/stock');
      const rows = unwrap<ShelfItem[]>(res.data);
      return Array.isArray(rows) ? rows : [];
    },
  });
}

/** One item's movements, newest first — only fetched for the docked item. */
export function useItemHistory(itemId: string | null) {
  return useQuery({
    queryKey: ['stock', 'history', itemId],
    enabled: !!itemId,
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<Movement[]> => {
      const res = await apiClient.get(`/stock/items/${itemId}/history`);
      const rows = unwrap<Movement[]>(res.data);
      return Array.isArray(rows) ? rows : [];
    },
  });
}

/** Set or change an item's minimum — the one edit the shelf itself allows. */
export function useSetMin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, minLevel, name }: { id: string; minLevel: number | null; name: string }) => {
      const res = await apiClient.patch(`/stock/items/${id}`, { name, minLevel: minLevel ?? undefined });
      return unwrap(res.data);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
  });
}

/** Take stock off the shelf for a reason that is not a patient. */
export function useWriteOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      itemId: string;
      lotId?: string;
      quantity: number;
      reason?: 'waste' | 'count' | 'return';
      note: string;
    }) => {
      const res = await apiClient.post('/stock/adjust', input);
      return unwrap(res.data);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
  });
}

/* ── dates ──────────────────────────────────────────────────────────────── */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Days from today to a `YYYY-MM-DD`, negative once it is past. */
export function daysTo(date: string | null): number | null {
  if (!date) return null;
  const then = new Date(`${date}T00:00:00`);
  if (Number.isNaN(then.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((then.getTime() - today.getTime()) / 86400_000);
}

/**
 * HOW CLOSE AN EXPIRY IS READ AS A COLOUR, and the board sets the thresholds:
 * `12 Sep` is red and `28 Sep` is amber on a board dated mid-September, so the
 * red band is the fortnight (and anything already past) and the amber one runs
 * to thirty days. Past thirty days the date is not news, and prints plain.
 */
export function expiryTone(date: string | null): 'danger' | 'warn' | null {
  const d = daysTo(date);
  if (d === null) return null;
  if (d <= 14) return 'danger';
  if (d <= 30) return 'warn';
  return null;
}

/**
 * "12 Sep" for something the clinic must act on this month, "03/2027" for a
 * date that is only a fact. A box expiring in 2027 does not need a day.
 */
export function expiryLabel(date: string | null): string {
  if (!date) return '—';
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  const near = daysTo(date);
  if (near !== null && near <= 30) return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

/** "30 Aug" — a movement's or a receipt's day. */
export function dayMonth(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "by Hossam N." — who made the entry, never mistakable for the patient. */
export function byActor(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  return ` · by ${parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0]}`;
}

/** The clinic's own words for why a number moved. */
export const REASON_LABEL: Record<string, string> = {
  receipt: 'received',
  dispense: 'used on a patient',
  count: 'count variance',
  transfer_out: 'transferred out',
  transfer_in: 'transferred in',
  waste: 'written off',
};

/* ── the catalog: definitions, not quantities (f2/f2b/f3) ─────────────────── */

export interface CatalogItem {
  id: string;
  name: string;
  category: string | null;
  unit: string | null;
  barcode: string | null;
  photoUrl: string | null;
  minLevel: number | null;
  batchTracked: boolean;
  sellable: boolean;
  orderCatalogItemId: string | null;
  /** What the clinic expects to pay, in minor units. */
  standardCostMinor: number | null;
  /** What the last priced delivery actually charged. */
  lastCostMinor: number | null;
  preferredSupplierId: string | null;
  preferredSupplierName: string | null;
  autoOrderEnabled: boolean;
  autoOrderThreshold: number | null;
  autoOrderQuantity: number | null;
  isActive: boolean;
  inStock: number;
  deactivatedAt: string | null;
  deactivatedBy: string | null;
  deactivatedReason: string | null;
}

/** Everything the clinic buys, active and deactivated — one read, two tabs. */
export function useCatalog() {
  return useQuery({
    queryKey: ['stock', 'catalog'],
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<CatalogItem[]> => {
      const res = await apiClient.get('/stock/catalog', { params: { includeInactive: 'true' } });
      const rows = unwrap<CatalogItem[]>(res.data);
      return Array.isArray(rows) ? rows : [];
    },
  });
}

export interface ItemDraft {
  name: string;
  category?: string | null;
  unit?: string | null;
  barcode?: string | null;
  minLevel?: number | null;
  batchTracked?: boolean;
  standardCostMinor?: number | null;
  preferredSupplierId?: string | null;
  autoOrderEnabled?: boolean;
  autoOrderThreshold?: number | null;
  autoOrderQuantity?: number | null;
  isActive?: boolean;
  deactivatedReason?: string | null;
}

/**
 * Create or edit a definition. The server takes only what is set, so an empty
 * field clears nothing it was not asked to clear — `null` is how this screen
 * says "remove it", and `undefined` means "leave it alone".
 */
export function useSaveItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...draft }: ItemDraft & { id?: string }) => {
      const body = Object.fromEntries(
        Object.entries(draft).filter(([, v]) => v !== undefined && v !== ''),
      );
      const res = id
        ? await apiClient.patch(`/stock/items/${id}`, body)
        : await apiClient.post('/stock/items', body);
      return unwrap<CatalogItem>(res.data);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }),
  });
}

/** Put a (already small) photo on an item. */
export async function uploadItemPhoto(itemId: string, photo: Blob): Promise<void> {
  const form = new FormData();
  form.append('file', photo, 'photo.jpg');
  await apiClient.post(`/stock/items/${itemId}/photo`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export interface Supplier {
  id: string;
  name: string;
  contactName?: string | null;
  contactPhone?: string | null;
  type?: string;
}

/**
 * The Directory's own suppliers. `stock:write` is what makes them visible —
 * the person who phones the supplier can see their own registry without being
 * handed the clinic's settings.
 */
export function useSuppliers() {
  return useQuery({
    queryKey: ['stock', 'suppliers'],
    staleTime: 300_000,
    retry: false,
    queryFn: async (): Promise<Supplier[]> => {
      const res = await apiClient.get('/external-providers', { params: { activeOnly: 'true' } });
      const rows = unwrap<Supplier[] | { data?: Supplier[] }>(res.data);
      const list = Array.isArray(rows) ? rows : (rows?.data ?? []);
      return list.filter((p) => p.type === 'supplier');
    },
  });
}

/** "8.50" from 850, and back. Minor units never reach a form field. */
export const majorOf = (minor: number | null | undefined): string =>
  minor == null ? '' : (minor / 100).toFixed(2);
export const minorOf = (major: string): number | null => {
  const n = Number(major.trim());
  return major.trim() === '' || Number.isNaN(n) ? null : Math.round(n * 100);
};

/* ── purchase orders: draft → ordered → confirmed → received (f5) ─────────── */

export type OrderStatus = 'draft' | 'ordered' | 'confirmed' | 'received' | 'cancelled';
export type OrderChannel = 'whatsapp' | 'email' | 'phone' | 'manual';

export interface OrderLine {
  id: string;
  itemId: string;
  itemName: string;
  unit: string | null;
  quantity: number;
  unitCostMinor: number | null;
  /** Why the line exists — `minimum`, `expiry`, or a person put it there. */
  reason: 'minimum' | 'expiry' | 'manual' | null;
  reasonNote: string | null;
  receivedQuantity: number;
}

export interface Order {
  id: string;
  number: string | null;
  status: OrderStatus;
  raisedBy: 'maie' | 'person';
  supplierId: string | null;
  supplierName: string | null;
  supplierAutoSend: boolean;
  supplierContact: string | null;
  sentAt: string | null;
  sentVia: OrderChannel | null;
  confirmedAt: string | null;
  receivedAt: string | null;
  nudgedAt?: string | null;
  /** Days since it was sent without a confirmation — what makes it age. */
  ageingDays: number | null;
  totalMinor: number;
  lines: OrderLine[];
  createdAt: string | null;
}

/**
 * Every order, with its drafts topped up first.
 *
 * ASSEMBLY RUNS ON OPEN. It is idempotent — one draft per supplier, and a line
 * somebody edited is never overwritten — so the screen can ask for it every
 * time rather than waiting on a nightly job that would leave the list stale
 * for most of the day it mattered.
 */
export function useOrders(canWrite: boolean) {
  return useQuery({
    queryKey: ['stock', 'orders'],
    staleTime: 15_000,
    retry: false,
    queryFn: async (): Promise<Order[]> => {
      if (canWrite) {
        await apiClient.post('/stock/orders/assemble').catch(() => undefined);
      }
      const res = await apiClient.get('/stock/orders');
      const rows = unwrap<Order[]>(res.data);
      return Array.isArray(rows) ? rows : [];
    },
  });
}

/** Every verb on an order, as one hook — they all invalidate the same list. */
export function useOrderActions() {
  const qc = useQueryClient();
  const done = { onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }) };
  return {
    setQuantity: useMutation({
      mutationFn: async ({ lineId, quantity }: { lineId: string; quantity: number }) => {
        await apiClient.patch(`/stock/orders/lines/${lineId}`, { quantity });
      },
      ...done,
    }),
    addLine: useMutation({
      mutationFn: async ({ orderId, itemId, quantity }: { orderId: string; itemId: string; quantity: number }) => {
        await apiClient.post(`/stock/orders/${orderId}/lines`, { itemId, quantity });
      },
      ...done,
    }),
    send: useMutation({
      mutationFn: async ({ orderId, via }: { orderId: string; via: OrderChannel }) => {
        const res = await apiClient.post(`/stock/orders/${orderId}/send`, { via });
        return unwrap<Order>(res.data);
      },
      ...done,
    }),
    /* The supplier's hands-free switch, reached from the order it is on. */
    autoSend: useMutation({
      mutationFn: async ({ orderId, on }: { orderId: string; on: boolean }) => {
        await apiClient.post(`/stock/orders/${orderId}/auto-send`, { on });
      },
      ...done,
    }),
    /* Chase a sent order on WhatsApp. */
    nudge: useMutation({
      mutationFn: async (orderId: string) => {
        await apiClient.post(`/stock/orders/${orderId}/nudge`);
      },
      ...done,
    }),
    confirm: useMutation({
      mutationFn: async (orderId: string) => {
        await apiClient.post(`/stock/orders/${orderId}/confirm`);
      },
      ...done,
    }),
    dismiss: useMutation({
      mutationFn: async (orderId: string) => {
        await apiClient.delete(`/stock/orders/${orderId}`);
      },
      ...done,
    }),
    createDraft: useMutation({
      mutationFn: async (supplierId: string) => {
        const res = await apiClient.post('/stock/orders', { supplierId });
        return unwrap<Order>(res.data);
      },
      ...done,
    }),
  };
}

/** "EGP 3,240" — an order's value, from minor units. */
export const orderMoney = (minor: number, currency = 'EGP'): string =>
  `${currency} ${(minor / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

/* ── counts: expected versus counted, note-gated variance (f6) ────────────── */

export type CountReason =
  | 'spillage'
  | 'breakage'
  | 'expired'
  | 'miscount'
  | 'theft'
  | 'found'
  | 'other';

export const COUNT_REASONS: Array<{ value: CountReason; label: string; hint?: string }> = [
  { value: 'miscount', label: 'Miscount', hint: 'the running number was wrong, nothing was lost' },
  { value: 'spillage', label: 'Spillage' },
  { value: 'breakage', label: 'Breakage' },
  { value: 'expired', label: 'Expired and thrown away' },
  { value: 'found', label: 'Found — it was there all along' },
  { value: 'theft', label: 'Missing' },
  { value: 'other', label: 'Something else' },
];

export interface CountLine {
  id: string;
  itemId: string;
  itemName: string;
  unit: string | null;
  batchTracked: boolean;
  expected: number;
  counted: number;
  variance: number;
  reason: CountReason | null;
  note: string | null;
  countedBy: string | null;
  countedAt: string | null;
}

export interface StockCount {
  id: string;
  status: 'open' | 'closed';
  startedBy: string | null;
  startedAt: string | null;
  closedBy: string | null;
  closedAt: string | null;
  counted: number;
  ofItems: number;
  variances: number;
  lines: CountLine[];
}

export interface CountSheetRow {
  itemId: string;
  name: string;
  unit: string | null;
  batchTracked: boolean;
  /** The running number, as it stands right now. */
  expected: number;
  /** Never counted and nothing on a shelf — this count IS its opening balance. */
  seedsOpening: boolean;
}

export function useCounts() {
  return useQuery({
    queryKey: ['stock', 'counts'],
    staleTime: 15_000,
    retry: false,
    queryFn: async (): Promise<StockCount[]> => {
      const res = await apiClient.get('/stock/counts');
      const rows = unwrap<StockCount[]>(res.data);
      return Array.isArray(rows) ? rows : [];
    },
  });
}

/** The shelf to walk — only fetched once a count is open and being worked. */
export function useCountSheet(countId: string | null) {
  return useQuery({
    queryKey: ['stock', 'count-sheet', countId],
    enabled: !!countId,
    staleTime: 10_000,
    retry: false,
    queryFn: async (): Promise<{ count: StockCount; shelf: CountSheetRow[] } | null> => {
      const res = await apiClient.get(`/stock/counts/${countId}/sheet`);
      return unwrap<{ count: StockCount; shelf: CountSheetRow[] }>(res.data);
    },
  });
}

export function useCountActions() {
  const qc = useQueryClient();
  const done = { onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }) };
  return {
    start: useMutation({
      mutationFn: async () => {
        const res = await apiClient.post('/stock/counts');
        return unwrap<StockCount>(res.data);
      },
      ...done,
    }),
    record: useMutation({
      mutationFn: async (input: {
        countId: string;
        itemId: string;
        counted: number;
        expected: number;
        reason?: CountReason;
        note?: string;
      }) => {
        const { countId, ...body } = input;
        await apiClient.post(`/stock/counts/${countId}/lines`, body);
      },
      ...done,
    }),
    removeLine: useMutation({
      mutationFn: async (lineId: string) => {
        await apiClient.delete(`/stock/counts/lines/${lineId}`);
      },
      ...done,
    }),
    close: useMutation({
      mutationFn: async (countId: string) => {
        const res = await apiClient.post(`/stock/counts/${countId}/close`);
        return unwrap<{ corrected: number; variances: number }>(res.data);
      },
      ...done,
    }),
  };
}

/* ── branches, and transfers between them (f7) ────────────────────────────── */

export interface BranchRow {
  id: string;
  name: string;
  code: string;
  isDefault: boolean;
  acceptingBookings: boolean;
}

/**
 * The clinic's sites, the default first.
 *
 * THE SWITCHER ONLY RENDERS ABOVE ONE BRANCH — the branches migration's own
 * rule, so a single-site clinic never learns the word.
 */
export function useBranches() {
  return useQuery({
    queryKey: ['branches'],
    staleTime: 300_000,
    retry: false,
    queryFn: async (): Promise<BranchRow[]> => {
      const res = await apiClient.get('/branches');
      const rows = unwrap<BranchRow[]>(res.data);
      return Array.isArray(rows) ? rows : [];
    },
  });
}

export type TransferStatus = 'draft' | 'in_transit' | 'received' | 'cancelled';

export interface TransferLine {
  id: string;
  itemId: string;
  itemName: string;
  unit: string | null;
  quantity: number;
  receivedQuantity: number;
  lotCode: string | null;
  expiresOn: string | null;
  note: string | null;
  shortfall: number;
}

export interface Transfer {
  id: string;
  number: string | null;
  status: TransferStatus;
  fromBranchId: string | null;
  fromBranch: string | null;
  toBranchId: string | null;
  toBranch: string | null;
  /** From the reader's branch: going out, coming in, or neither. */
  direction: 'out' | 'in' | 'other';
  sentAt: string | null;
  sentBy: string | null;
  receivedAt: string | null;
  receivedBy: string | null;
  items: number;
  lines: TransferLine[];
  createdAt: string | null;
}

export function useTransfers(branchId: string | null) {
  return useQuery({
    queryKey: ['stock', 'transfers', branchId],
    staleTime: 15_000,
    retry: false,
    queryFn: async (): Promise<Transfer[]> => {
      const res = await apiClient.get('/stock/transfers', {
        params: branchId ? { branchId } : undefined,
      });
      const rows = unwrap<Transfer[]>(res.data);
      return Array.isArray(rows) ? rows : [];
    },
  });
}

export function useTransferActions() {
  const qc = useQueryClient();
  const done = { onSuccess: () => void qc.invalidateQueries({ queryKey: ['stock'] }) };
  return {
    createDraft: useMutation({
      mutationFn: async (input: { toBranchId: string; fromBranchId?: string }) => {
        const res = await apiClient.post('/stock/transfers', input);
        return unwrap<Transfer>(res.data);
      },
      ...done,
    }),
    addLine: useMutation({
      mutationFn: async (input: { transferId: string; itemId: string; quantity: number; lotId?: string }) => {
        const { transferId, ...body } = input;
        await apiClient.post(`/stock/transfers/${transferId}/lines`, body);
      },
      ...done,
    }),
    removeLine: useMutation({
      mutationFn: async (lineId: string) => {
        await apiClient.delete(`/stock/transfers/lines/${lineId}`);
      },
      ...done,
    }),
    send: useMutation({
      mutationFn: async (transferId: string) => {
        const res = await apiClient.post(`/stock/transfers/${transferId}/send`);
        return unwrap<Transfer>(res.data);
      },
      ...done,
    }),
    confirm: useMutation({
      mutationFn: async (input: {
        transferId: string;
        lines: Array<{ lineId: string; quantity: number; note?: string }>;
      }) => {
        const res = await apiClient.post(`/stock/transfers/${input.transferId}/confirm`, {
          lines: input.lines,
        });
        return unwrap<Transfer>(res.data);
      },
      ...done,
    }),
    dismiss: useMutation({
      mutationFn: async (transferId: string) => {
        await apiClient.delete(`/stock/transfers/${transferId}`);
      },
      ...done,
    }),
  };
}

/* ── who holds the stock module (one per branch) ──────────────────────────── */

export interface StockHolderPerson {
  userId: string;
  name: string;
  role: string;
}

export interface StockHolder {
  branchId: string | null;
  holder: StockHolderPerson | null;
  byDefault: boolean;
}

export function useStockHolder() {
  return useQuery({
    queryKey: ['stock', 'holder'],
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<StockHolder> => {
      const res = await apiClient.get('/stock/holder');
      return unwrap<StockHolder>(res.data);
    },
  });
}

export function useHolderCandidates(enabled: boolean) {
  return useQuery({
    queryKey: ['stock', 'holder', 'candidates'],
    enabled,
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<StockHolderPerson[]> => {
      const res = await apiClient.get('/stock/holder/candidates');
      return unwrap<StockHolderPerson[]>(res.data);
    },
  });
}

export function useReassignHolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { toUserId: string; expectedUserId?: string }) => {
      const res = await apiClient.post('/stock/holder/reassign', input);
      return unwrap<StockHolder>(res.data);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['stock', 'holder'] }),
  });
}
